// client/src/utils/structureGeometry.js
// Итерация 8.1: утилиты для работы с installed_modules.
//
// - distance3D: расстояние между двумя точками.
// - filterByRadius: фильтр модулей по радиусу от персонажа.
// - findCurrentModule: nearest neighbor — модуль, где стоит персонаж.
// - filterByDeck: фильтр по currentDeckId.
// - prepareModules: цепочка (deck → radius → valid position).
//
// ⚠️ Чистые функции — без React, без Three.js, без window.
//    Портируется в C++ (Unreal) за полдня.

// ============================================================================
// 1. ВАЛИДАЦИЯ
// ============================================================================

/**
 * Проверяет, что у модуля есть валидная позиция.
 */
export function hasValidPosition(module) {
  const p = module?.position;
  return !!(
    p &&
    typeof p === 'object' &&
    typeof p.x === 'number' &&
    Number.isFinite(p.x) &&
    typeof p.y === 'number' &&
    Number.isFinite(p.y) &&
    typeof p.z === 'number' &&
    Number.isFinite(p.z)
  );
}

// ============================================================================
// 2. РАССТОЯНИЕ
// ============================================================================

/**
 * Евклидово расстояние между двумя точками {x,y,z}.
 * Возвращает Infinity, если хотя бы одна точка невалидна.
 */
export function distance3D(a, b) {
  if (!a || !b) return Infinity;
  if (
    typeof a.x !== 'number' ||
    typeof a.y !== 'number' ||
    typeof a.z !== 'number' ||
    typeof b.x !== 'number' ||
    typeof b.y !== 'number' ||
    typeof b.z !== 'number'
  ) {
    return Infinity;
  }
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Квадрат расстояния (быстрее, без sqrt). Для сравнения «ближе/дальше».
 */
export function distance3DSquared(a, b) {
  if (!a || !b) return Infinity;
  const dx = (a.x ?? 0) - (b.x ?? 0);
  const dy = (a.y ?? 0) - (b.y ?? 0);
  const dz = (a.z ?? 0) - (b.z ?? 0);
  return dx * dx + dy * dy + dz * dz;
}

// ============================================================================
// 3. ФИЛЬТР ПО ПАЛУБЕ
// ============================================================================

/**
 * Оставляет только модули на указанной палубе.
 * Если currentDeckId = null/undefined — возвращает все модули.
 */
export function filterByDeck(modules, currentDeckId) {
  if (!Array.isArray(modules)) return [];
  if (!currentDeckId) return modules;
  return modules.filter((m) => m?.deck_id === currentDeckId);
}

// ============================================================================
// 4. ФИЛЬТР ПО РАДИУСУ
// ============================================================================

/**
 * Оставляет только модули в радиусе radius от characterPosition.
 * Если radius <= 0 или characterPosition невалидна — возвращает все модули.
 */
export function filterByRadius(modules, characterPosition, radius) {
  if (!Array.isArray(modules)) return [];
  if (!radius || radius <= 0) return modules;
  if (!characterPosition) return modules;

  const r2 = radius * radius;

  return modules.filter((m) => {
    if (!hasValidPosition(m)) return false;
    return distance3DSquared(m.position, characterPosition) <= r2;
  });
}

// ============================================================================
// 5. NEAREST NEIGHBOR (currentModuleId)
// ============================================================================

/**
 * Находит модуль, ближайший к characterPosition.
 * Используется для подсветки модуля, в котором стоит персонаж.
 *
 * @param {Array} modules — массив installed_modules
 * @param {{x,y,z}} characterPosition — позиция персонажа (относительно структуры)
 * @returns {Object|null} — ближайший модуль или null
 */
export function findCurrentModule(modules, characterPosition) {
  if (!Array.isArray(modules) || modules.length === 0) return null;
  if (!characterPosition) return null;

  let best = null;
  let bestDist = Infinity;

  for (const m of modules) {
    if (!hasValidPosition(m)) continue;
    const d = distance3DSquared(m.position, characterPosition);
    if (d < bestDist) {
      bestDist = d;
      best = m;
    }
  }

  return best;
}

// ============================================================================
// 6. ГЛАВНАЯ ЦЕПОЧКА
// ============================================================================

/**
 * Готовит модули для рендера:
 *   1. Фильтр по палубе (если currentDeckId задан).
 *   2. Фильтр по радиусу (если characterPosition + radius заданы).
 *   3. Отбрасывает модули без валидной позиции.
 *
 * @param {Array} modules — installed_modules
 * @param {string|null} currentDeckId
 * @param {{x,y,z}|null} characterPosition
 * @param {number} radius — 0 = без ограничения
 * @returns {Array}
 */
export function prepareModules(modules, currentDeckId, characterPosition, radius) {
  if (!Array.isArray(modules)) return [];

  // 1. Валидная позиция
  let result = modules.filter(hasValidPosition);

  // 2. Палуба
  result = filterByDeck(result, currentDeckId);

  // 3. Радиус (только если палуба пустая ИЛИ модулей > 200)
  if (characterPosition && radius > 0 && result.length > 200) {
    result = filterByRadius(result, characterPosition, radius);
  }

  return result;
}

// ============================================================================
// 7. АГРЕГАТЫ (для отладки, UI)
// ============================================================================

/**
 * Статистика по массиву модулей.
 */
export function getModulesStats(modules) {
  if (!Array.isArray(modules)) {
    return { total: 0, withPosition: 0, decks: 0, purposes: {} };
  }

  const withPosition = modules.filter(hasValidPosition).length;

  const decksSet = new Set();
  const purposes = {};

  for (const m of modules) {
    if (m?.deck_id) decksSet.add(m.deck_id);
    if (m?.purpose) {
      purposes[m.purpose] = (purposes[m.purpose] || 0) + 1;
    }
  }

  return {
    total: modules.length,
    withPosition,
    decks: decksSet.size,
    purposes,
  };
}

// ============================================================================
// ЭКСПОРТ ПО УМОЛЧАНИЮ (для удобства)
// ============================================================================

export default {
  hasValidPosition,
  distance3D,
  distance3DSquared,
  filterByDeck,
  filterByRadius,
  findCurrentModule,
  prepareModules,
  getModulesStats,
};
